import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ToastCtx = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((opts) => {
    const id = nextId++;
    const toast = {
      id,
      kind: opts.kind || "info", // info | success | warn | error
      title: opts.title || "",
      message: opts.message || "",
      duration: opts.duration ?? 4000,
      action: opts.action || null,
    };
    setToasts((ts) => [...ts, toast]);
    if (toast.duration > 0) {
      setTimeout(() => remove(id), toast.duration);
    }
    return id;
  }, [remove]);

  // Convenience helpers
  const api = {
    show,
    success: (msg, opts = {}) => show({ ...opts, kind: "success", message: msg }),
    error:   (msg, opts = {}) => show({ ...opts, kind: "error",   message: msg, duration: 6000 }),
    warn:    (msg, opts = {}) => show({ ...opts, kind: "warn",    message: msg }),
    info:    (msg, opts = {}) => show({ ...opts, kind: "info",    message: msg }),
    remove,
  };

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <ToastContainer toasts={toasts} onRemove={remove} />
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

function ToastContainer({ toasts, onRemove }) {
  return (
    <div style={{
      position: "fixed",
      top: 16,
      right: 16,
      zIndex: 9999,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      pointerEvents: "none",
      maxWidth: 380,
    }}>
      <AnimatePresence>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onRemove={() => onRemove(t.id)} />
        ))}
      </AnimatePresence>
    </div>
  );
}

const KIND_STYLES = {
  info:    { color: "var(--text)",     border: "var(--line-strong)", accent: "var(--text-dim)" },
  success: { color: "var(--ok)",       border: "rgba(74,222,128,0.35)", accent: "var(--ok)" },
  warn:    { color: "var(--warn)",     border: "rgba(251,191,36,0.35)", accent: "var(--warn)" },
  error:   { color: "var(--err)",      border: "rgba(248,113,113,0.35)", accent: "var(--err)" },
};

function ToastItem({ toast, onRemove }) {
  const style = KIND_STYLES[toast.kind] || KIND_STYLES.info;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 40, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 40, scale: 0.95 }}
      transition={{ duration: 0.25 }}
      style={{
        background: "var(--bg-elev)",
        border: `1px solid ${style.border}`,
        borderLeft: `3px solid ${style.accent}`,
        borderRadius: "var(--r)",
        padding: "12px 16px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        pointerEvents: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        cursor: toast.action ? "pointer" : "default",
      }}
      onClick={() => {
        if (toast.action?.onClick) toast.action.onClick();
        onRemove();
      }}
    >
      {toast.title && (
        <div style={{
          fontSize: 11,
          fontFamily: "var(--mono)",
          color: style.color,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontWeight: 600,
        }}>
          {toast.title}
        </div>
      )}
      <div style={{
        fontSize: 13,
        color: "var(--text)",
        lineHeight: 1.45,
      }}>
        {toast.message}
      </div>
      {toast.action?.label && (
        <div style={{
          fontSize: 11,
          fontFamily: "var(--mono)",
          color: style.accent,
          marginTop: 4,
          letterSpacing: "0.05em",
        }}>
          → {toast.action.label}
        </div>
      )}
    </motion.div>
  );
}
